import { useQuery } from '@apollo/client/react';

import { clsx } from 'clsx';

import { use, useEffect } from 'react';

import { nonFalsey } from 'Utils/nonFalsey';

import { usePusherHandler } from '@/hooks/usePusherHandler';

import { graphql } from '@/gql';

import type { UserShortcutDataFragmentFragment } from '@/gql/graphql';

import { maxWashingLineLength } from '@/app/(main)/user/[userId]/_components/settings/washing-line/views/utils';
import { env } from '@/env';

import { Or2AnalyticsContext } from '@/context/Or2AnalyticsContext';

import { CurrentUserContext } from '@/context/CurrentUserContext';

import type { Shortcut } from '../ShortcutIcon';

export const SHORTCUTS_QUERY = graphql(`
  query Shortcuts($client: String!) {
    currentUser {
      _id
      firstName
      lastName
      email
      intercomHash {
        hash
      }
      isMyHubEnabled
      shortcutSettingsWithServiceGroups(client: $client) {
        ...UserShortcutDataFragment
      }
    }
  }
`);

const newsShortcut = {
  _id: 'news',
  name: 'News',
  icon: 'chrome_reader_mode',
  basePath: '/news',
  path: '/news',
  serviceAdminShortcut: false,
};

const selectGroupAndServiceShortcut = {
  _id: 'select-groups-and-services',
  name: 'My Groups',
  basePath: '/quickLinks',
  path: '/quickLinks',
  icon: 'list',
  serviceAdminShortcut: false,
};

const roostShortcut = {
  _id: 'roost',
  name: 'The Roost',
  basePath: '/roost',
  path: '/roost',
  icon: 'admin_panel_settings',
  serviceAdminShortcut: false,
};

export const isMultiGroupViewShortcut = (_id: string | null | undefined) => {
  return _id && _id === 'my-hub';
};

export function useShortcuts() {
  const { data, loading, refetch } = useQuery(SHORTCUTS_QUERY, {
    variables: {
      client: env.NEXT_PUBLIC_CLIENT_NAME,
    },
  });

  const { currentUser } = data ?? {};
  const { firstName, lastName, email, intercomHash } = currentUser ?? {};
  const { hash } = intercomHash ?? {};

  const user = data?.currentUser;
  const userId = user?._id;
  const userName = `${firstName ?? ''} ${lastName ?? ''}`;
  const userEmail = email ?? '';
  const userIntercomHash = hash ?? '';
  const isMyHubEnabled = Boolean(user?.isMyHubEnabled);

  const { isSuperuser } = use(CurrentUserContext);

  usePusherHandler(
    userId ? [`user-${userId}`] : null,
    'permissions-updated',
    refetch,
  );

  const { analytics } = use(Or2AnalyticsContext);

  const userShortcuts = user?.shortcutSettingsWithServiceGroups || [];

  // For the default view
  const defaultViewShortcuts = userShortcuts.filter(
    (shortcut) => !shortcut?.serviceAdminShortcut,
  );

  const isCustomized = userShortcuts.some(
    (shortcut) => shortcut?.userWashingLineState === 'pinned',
  );

  const initialShortcuts: Shortcut[] = [newsShortcut];
  const initialLoadedShortcuts: Shortcut[] = [];

  const tKShortcut = userShortcuts.find(
    (shortcut) => shortcut?._id === 'the-key',
  );

  const hasTKShortcut = Boolean(tKShortcut);

  if (hasTKShortcut) {
    const knowledgeShortcut = {
      _id: tKShortcut?._id || '',
      name: tKShortcut?.name || '',
      url: tKShortcut?.url || '',
      iconUrl: tKShortcut?.iconUrl || '',
      serviceAdminShortcut: false,
    };

    initialLoadedShortcuts.push(knowledgeShortcut);
  }

  const userShortcutsWithoutKnowledge = userShortcuts.filter(
    (shortcut) => shortcut?._id !== 'the-key',
  );

  const defaultViewShortcutsWithoutKnowledge = defaultViewShortcuts.filter(
    (shortcut) => shortcut?._id !== 'the-key',
  );

  const shortcutsWithoutSelectGroupsAndServices =
    (isCustomized
      ? userShortcutsWithoutKnowledge
      : defaultViewShortcutsWithoutKnowledge
    )
      ?.filter((shortcut) => {
        return shortcut?._id !== 'select-groups-and-services';
      })
      .filter(nonFalsey) || [];

  const filteredShortcuts = isCustomized
    ? userShortcutsWithoutKnowledge.filter(
        (shortcut) => shortcut?.userWashingLineState === 'pinned',
      )
    : shortcutsWithoutSelectGroupsAndServices;

  const multiGroupViewShortcutName = firstName
    ? firstName + "'s Hub"
    : 'My Hub';

  if (isMyHubEnabled) {
    const multiGroupViewShortcut = {
      _id: 'my-hub',
      name: multiGroupViewShortcutName,
      icon: 'home',
      basePath: '/myHub',
      path: '/myHub',
      serviceAdminShortcut: false,
    };

    initialShortcuts.unshift(multiGroupViewShortcut);
  }

  const hasSelectGroupAndServicesShortcut =
    shortcutsWithoutSelectGroupsAndServices.length + initialShortcuts.length >
    maxWashingLineLength;

  // we shrink down the number of shortcuts if there are loads of them
  // by compressing all the boards, groups and Services into 1
  const shortcuts: Shortcut[] = user
    ? hasSelectGroupAndServicesShortcut && !isCustomized
      ? [
          ...initialShortcuts,
          ...initialLoadedShortcuts,
          selectGroupAndServiceShortcut,
          ...filteredShortcuts
            .filter((shortcut) => {
              return shortcut?.linkedObject === null;
            })
            .map(mapUserShortcuts)
            .filter(nonFalsey),
        ]
      : [
          ...initialShortcuts,
          ...initialLoadedShortcuts,
          ...filteredShortcuts.map(mapUserShortcuts).filter(nonFalsey),
        ]
    : [];

  if (isSuperuser) {
    shortcuts.unshift(roostShortcut);
  }

  // Configures user's identity in Or2Analytics
  useEffect(() => {
    if (userId && analytics) {
      analytics.identify(
        userId,
        {
          email: userEmail,
          name: userName,
        },
        {
          Intercom: {
            user_hash: userIntercomHash,
            has_ksg_shortcut: hasTKShortcut,
            has_my_hub: isMyHubEnabled,
            has_select_group_icon: hasSelectGroupAndServicesShortcut,
          },
        },
      );
    }
  }, [
    userId,
    analytics,
    userEmail,
    userName,
    userIntercomHash,
    hasTKShortcut,
    isMyHubEnabled,
    hasSelectGroupAndServicesShortcut,
  ]);

  return { shortcuts, loading: loading && !data };
}

function mapUserShortcuts(
  shortcut: UserShortcutDataFragmentFragment | null,
): Shortcut | null {
  if (!shortcut) {
    return null;
  }

  if (shortcut?._id === 'select-groups-and-services') {
    return selectGroupAndServiceShortcut;
  }

  if (shortcut.url) {
    const { url, name, iconUrl } = shortcut;
    if (!(name && iconUrl)) {
      return null;
    }

    return {
      _id: url,
      name,
      iconUrl,
      url,
    };
  }

  switch (shortcut.linkedObject?.__typename) {
    case 'Service': {
      const { name } = shortcut;
      const { machineName } = shortcut.linkedObject || {};
      if (!(name && machineName)) {
        return null;
      }

      return {
        _id: shortcut._id,
        name,
        basePath: '/service',
        params: {},
        path: `/s/${encodeURI(machineName)}`,
        icon: 'school',
        className: 'service',
        iconUrl: shortcut?.linkedObject.iconUrl,
        groupOrServiceName: name,
      };
    }

    case 'Group': {
      const { name } = shortcut;
      const { machineName } = shortcut.linkedObject;
      if (!(name && machineName)) {
        return null;
      }

      const groupType = shortcut?.linkedObject?.type;

      return {
        _id: shortcut._id,
        name,
        basePath: `/g/${encodeURI(machineName)}`,
        params: {},
        path: `/g/${encodeURI(machineName)}`,
        icon: 'group',
        className: clsx('group', {
          hasPosts: groupType?.hasPosts,
          hasFiles: groupType?.hasFiles,
          hasEvents: groupType?.hasEvents,
        }),
        iconUrl: shortcut?.linkedObject.iconUrl,
        groupOrServiceName: name,
      };
    }

    default:
      return null;
  }
}
